/**
 * The fx visuals — the shader-pack library (World mode 'fx', worldProtocol 1.4, rendered by the engine's
 * FxVisualMode), in two delivery tiers:
 *
 *  - CORE (≈150): bundled with every app. Each package is a static file, public/fx/<pack>/<slug>.json,
 *    same-origin everywhere a page runs (syqel.art, the desktop shell, the iOS AssetServer, the Android
 *    TV / phone asset servers, the webOS service all serve this build's dist/) — they work offline.
 *  - EXTENDED (the rest): fetched from the fx asset base (fxBase.ts) when one mounts. They are OFFERED
 *    only while that base answers (fxExtendedReachable) — offline, every picker, grid and Director pool
 *    leaves them out (catalogue.ts), so the app never mounts a visual it cannot load.
 *
 * Each World carries `fx: { url }`, so registering all of them costs only descriptors, and a package is
 * fetched when its world actually mounts (the engine bounds the load: 12 s of INACTIVITY → modeError →
 * the host's fallback, and a late arrival recovers). Nothing here touches the OTA registry — a future
 * `world:<pack>/<slug>` OTA module with the same id wins (see registerFxWorlds).
 *
 * The descriptors (fxWorlds.json) and the core packages are GENERATED from the shader-pack release
 * branch + the content curation by scripts/fx/build-fx-packs.mjs — never edit either by hand.
 *
 * ONE EXCEPTION to lazy loading: the DEFAULT world (volumes.ts DEFAULT_WORLD_ID). FxVisualMode draws
 * nothing until a `{ url }` package has landed and compiled, and the host shows the opening world as
 * it is — so a lazy default would open the app on black for the fetch. If it is an fx world, its
 * package is in the bundle (fxDefault.json, also generated) and registered inline.
 */
import type { World } from '@syqel/engine'
import { Engine } from '@syqel/ota-client'
import generated from './fxWorlds.json'
import inlineDefault from './fxDefault.json'
import { fxExtBase } from './fxBase'
import integrity from './fxIntegrity.json'
import templates from './fxTemplates.json'
import { sha256Hex } from './sha256'

type FxDescriptor = World & { fx: { url: string } }
const CORE = generated.worlds as unknown as FxDescriptor[]
const EXTENDED = generated.extended as unknown as FxDescriptor[]

/** The extended fx base this build loads from (VITE_FX_EXT_BASE, else the default). */
export const FX_EXT_BASE = fxExtBase(import.meta.env as { VITE_FX_EXT_BASE?: string })

/** Ids of the EXTENDED fx visuals (not bundled — need the fx base). */
export const EXTENDED_FX_IDS: ReadonlySet<string> = new Set(EXTENDED.map((w) => w.id))
/** Ids of the fx visuals too heavy for the weak TV tiers (curation quality `slow`). */
export const SLOW_FX_IDS: ReadonlySet<string> = new Set(generated.slow)

/** Every fx World descriptor, its url resolved: core against this build's base, extended against the fx
 *  base (the build-time catalogue reads these too — vite.config.ts). */
export function fxWorlds(base: string = import.meta.env.BASE_URL, extBase: string = FX_EXT_BASE): World[] {
  return [
    ...CORE.map((w) => w.id === inlineDefault.id && inlineDefault.pkg ? { ...w, fx: inlineDefault.pkg } as World : { ...w, fx: { url: `${base}${w.fx.url}` } }),
    ...EXTENDED.map((w) => ({ ...w, fx: { url: `${extBase}${w.fx.url}` } }))
  ]
}

/** Ids of the fx visuals that HAVE a 16:9 landscape still (the set is partial — ~1 in 10). */
const LANDSCAPE_FX_IDS: ReadonlySet<string> = new Set(generated.landscape)

/** A world's stills for a 16:9 tile, best first: its landscape preview where it has one, then (an fx
 *  visual) its 9:16 portrait thumb (cover-cropped — every fx visual has one). Bundled for everything but
 *  an extended fx visual, whose stills live beside its package on the fx base. ONLY stills that exist:
 *  the generator knows which do (fxWorlds.json `landscape`), so a tile never asks for a missing one —
 *  guessing used to cost a 404 + a console error per tile, ~125 of them every time a TV opened its grid
 *  (each relayed through the Android shell's log bridge). A tile still falls back on a load error (an
 *  unreachable fx base). */
export function previewUrls(id: string): string[] {
  if (!id.includes('/')) return [`previews/${id}.jpg`]
  const base = EXTENDED_FX_IDS.has(id) ? FX_EXT_BASE : ''
  return [...(LANDSCAPE_FX_IDS.has(id) ? [`${base}previews/${id}.jpg`] : []), `${base}previews-9x16/${id}.thumb.webp`]
}

/**
 * Register the fx worlds (core + extended) with the CURRENT engine. Call after initOta (an OTA
 * engine-core swap replaces the WORLDS table, so registering before it would be lost). An id the
 * library already has — an OTA package that arrived for it — is left alone: the signed registry
 * outranks the bundled copy. Registering an extended world is free (a descriptor); whether it is
 * OFFERED is the reachability below. Also starts watching that reachability.
 */
export function registerFxWorlds(): void {
  // THE OWNER'S TEMPLATES (which instrument owns which effect slot, per visual type) — registered once, before
  // anything mounts, into the engine that is ACTIVE (the bundled one or an OTA engine-core the cache swapped in:
  // the registry lives in the engine instance). The packs already carry the resolved rows, so an engine older
  // than engineApi 1.24 (no registry) still plays them as shipped.
  if (typeof Engine.setTemplates === 'function' && templates) {
    Engine.setTemplates(templates as Parameters<typeof Engine.setTemplates>[0])
    // what the ENGINE now holds (its registry, read back) — not what was handed to it
    const held = Object.keys((templates as { templates: object }).templates).filter((type) => Engine.getTemplate?.(type))
    console.log(`[syqel] templates: ${held.join(', ')}`)
  }
  const have = new Set(Engine.WORLDS.map((w) => w.id))
  for (const w of fxWorlds()) if (!have.has(w.id)) Engine.registerWorld(w)
  // the fx family, with THE loader every surface shares (web, desktop, phones, TV receivers, Tizen — they all
  // run this page): a remote package mounts only once its bytes match the bundled sha256
  Engine.registerVisualFactory('fx', (w) => new Engine.FxVisualMode(w as never, { load: verifiedFxLoad }))
  watchFxBase()
}

// ── INTEGRITY ─────────────────────────────────────────────────────────────────────────────────────────
// A remote fx package is SHADER CODE the GPU compiles. The base is a CDN we don't run the app from, so the
// app trusts no byte of it it didn't expect: fxIntegrity.json (generated with the packages) carries the full
// sha256 of every extended package, and the loader verifies the fetched bytes against it BEFORE the engine
// parses or compiles anything. A mismatch (a tampered or stale object, a wrong base) is refused — the engine
// reports a modeError and the host falls back — and the visual is HIDDEN for the rest of the run (catalogue
// loadable()), so neither a picker nor the Director offers it again. Bundled (core) packages are part of the
// app and load as they are; stills are images, not code, and are not verified.
const EXPECTED: Readonly<Record<string, string>> = (integrity as { sha256: Record<string, string> }).sha256
const refused = new Set<string>()

/** Was this extended visual refused (its package didn't match the app's index)? */
export function fxRefused(id: string): boolean {
  return refused.has(id)
}

// The extended visual each package url on the fx base belongs to — from the app's own descriptors, not
// parsed out of the url. A url on the base that names no descriptor has no expected hash, so it is refused.
const EXTENDED_ID_BY_PATH: ReadonlyMap<string, string> = new Map(EXTENDED.map((w) => [w.fx.url, w.id]))

async function verifiedFxLoad(url: string, progress: () => void): Promise<unknown> {
  const res = await fetch(url)
  progress()
  if (!res.ok) throw new Error(`fx: ${url} → ${res.status}`)
  const bytes = new Uint8Array(await res.arrayBuffer())
  progress()
  if (url.startsWith(FX_EXT_BASE)) {
    const path = url.slice(FX_EXT_BASE.length)
    const known = EXTENDED_ID_BY_PATH.get(path)
    const id = known ?? path
    const want = known ? EXPECTED[known] : undefined
    const got = await sha256Hex(bytes)
    if (!want || got !== want) {
      if (!refused.has(id)) {
        refused.add(id)
        console.warn(`[syqel] fx package REFUSED — ${id} doesn't match the app's index (got ${got.slice(0, 12)}, want ${want ? want.slice(0, 12) : 'nothing'}); hidden`)
        for (const cb of [...listeners]) { try { cb() } catch { /* a listener must not break the rest */ } }
      }
      throw new Error(`fx: ${id} failed its integrity check`)
    }
  }
  return JSON.parse(new TextDecoder().decode(bytes))
}

// ── EXTENDED REACHABILITY ────────────────────────────────────────────────────────────────────────────
// The fx base is reachable when its index.json answers as ITSELF: JSON (by content-type), that parses, naming
// the base's format and a version this app reads. A status alone proves nothing — a static host's SPA
// fallback answers any missing path with 200 text/html (syqel.art/fx-ext/ did, so the app called the set
// online and every remote still failed to decode). Probed once at boot, again whenever the
// browser says the network came back, and every RETRY_MS while it is down (a native webview's
// navigator.onLine can lie — a phone on a captive Wi-Fi says online). Offline/unknown = not offered:
// at boot the extended visuals appear a moment after the core set, never the other way round.
const RETRY_MS = 60_000
const PROBE_TIMEOUT_MS = 6_000
let reachable = false
let watching = false
let retry = 0
const listeners = new Set<() => void>()

/** Are the extended fx visuals loadable right now? */
export function fxExtendedReachable(): boolean {
  return reachable
}

/** Subscribe to reachability flips (catalogue.ts turns them into its offer-change signal). */
export function onFxReachabilityChange(cb: () => void): () => void {
  listeners.add(cb)
  return () => { listeners.delete(cb) }
}

function setReachable(on: boolean): void {
  window.clearTimeout(retry)
  if (!on) retry = window.setTimeout(probe, RETRY_MS)
  if (on === reachable) return
  reachable = on
  console.log(`[syqel] extended fx visuals ${on ? 'reachable' : 'unreachable'} (${FX_EXT_BASE})`)
  for (const cb of [...listeners]) { try { cb() } catch { /* a listener must not break the rest */ } }
}

const FX_EXT_FORMAT = 'syqel-fx-ext'
const FX_EXT_VERSION = 1

/** Is this response the fx base's own index (see above)? */
export async function isFxBaseIndex(r: Response): Promise<boolean> {
  if (!r.ok || !/^application\/json\b/i.test(r.headers.get('content-type') ?? '')) return false
  try {
    const j = (await r.json()) as { format?: unknown; version?: unknown }
    return j.format === FX_EXT_FORMAT && j.version === FX_EXT_VERSION
  } catch { return false }
}

function probe(): void {
  if (!EXTENDED.length) return
  if (typeof navigator !== 'undefined' && navigator.onLine === false) { setReachable(false); return }
  const ctl = typeof AbortController !== 'undefined' ? new AbortController() : null
  const stop = abortAfterResponsiveMs(PROBE_TIMEOUT_MS, () => ctl?.abort())
  fetch(`${FX_EXT_BASE}index.json`, { cache: 'no-store', signal: ctl?.signal })
    .then(isFxBaseIndex)
    .then(setReachable, () => setReachable(false))
    .finally(stop)
}

// THE PROBE'S TIMEOUT COUNTS ONLY TIME THE PAGE COULD HAVE HEARD AN ANSWER. A plain setTimeout counts
// wall-clock — including a main-thread stall (a first world's build on a weak TV stalls the page 7 s and
// more): the answer arrived during the stall, but the overdue timer ran first and aborted it, the set read as
// unreachable, and the next probe was a minute away. So the deadline advances in short ticks, and a tick that
// lands late adds only its nominal length — a stall is not the network being slow.
const PROBE_TICK_MS = 250
function abortAfterResponsiveMs(ms: number, abort: () => void): () => void {
  let waited = 0
  let last = performance.now()
  const tick = window.setInterval(() => {
    const now = performance.now()
    waited += Math.min(now - last, 2 * PROBE_TICK_MS)
    last = now
    if (waited >= ms) { window.clearInterval(tick); abort() }
  }, PROBE_TICK_MS)
  return () => window.clearInterval(tick)
}

function watchFxBase(): void {
  if (watching || typeof window === 'undefined') return
  watching = true
  window.addEventListener('online', probe)
  window.addEventListener('offline', () => setReachable(false))
  probe()
}
